import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

export interface CollisionItem {
  file: File;
  originalName: string;
  suggestedName: string;
}

type Decision = 'pending' | 'keep-both' | 'skipped';

interface BatchCollisionDialogProps {
  open: boolean;
  collisions: CollisionItem[];
  onResolve: (resolved: Array<{ file: File; fileName: string }>) => void;
  onCancel: () => void;
}

export function BatchCollisionDialog({
  open,
  collisions,
  onResolve,
  onCancel,
}: BatchCollisionDialogProps) {
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  const updateDecision = (originalName: string, decision: Decision) => {
    setDecisions(prev => ({ ...prev, [originalName]: decision }));
  };

  const allDecided = collisions.every(
    c => (decisions[c.originalName] ?? 'pending') !== 'pending'
  );

  const handleContinue = () => {
    const resolved = collisions
      .filter(c => decisions[c.originalName] === 'keep-both')
      .map(c => ({ file: c.file, fileName: c.suggestedName }));
    setDecisions({});
    onResolve(resolved);
  };

  const handleCancel = () => {
    setDecisions({});
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      data-testid="batch-collision-dialog"
    >
      <DialogTitle>Some files already exist</DialogTitle>
      <DialogContent>
        {collisions.map(collision => {
          const decision = decisions[collision.originalName] ?? 'pending';
          return (
            <Box
              key={collision.originalName}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                '&:not(:last-child)': {
                  borderBottom: 1,
                  borderColor: 'divider',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mr: 1,
                  color:
                    decision === 'skipped' ? 'text.disabled' : 'text.primary',
                  textDecoration:
                    decision === 'skipped' ? 'line-through' : 'none',
                }}
              >
                {collision.originalName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button
                  data-testid="collision-keep-both"
                  size="small"
                  variant={decision === 'keep-both' ? 'contained' : 'outlined'}
                  onClick={() =>
                    updateDecision(collision.originalName, 'keep-both')
                  }
                >
                  Keep both
                </Button>
                <Button
                  data-testid="collision-skip"
                  size="small"
                  variant={decision === 'skipped' ? 'contained' : 'outlined'}
                  onClick={() =>
                    updateDecision(collision.originalName, 'skipped')
                  }
                >
                  Skip
                </Button>
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          data-testid="collision-continue"
          variant="contained"
          onClick={handleContinue}
          disabled={!allDecided}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
